import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PublishIcon from '@mui/icons-material/Publish';
import { supabase } from '../config/supabase';

type CalendarPublicationStatus = {
  subscriberCount: number;
  pendingCount: number;
  status: 'up_to_date' | 'updates_pending' | 'cancellation_pending';
  unreadyReason?: string | null;
};

type CalendarPublicationPanelProps = {
  kind: 'match' | 'event';
  id: string;
  readOnly?: boolean;
  onMessage?: (severity: 'success' | 'warning' | 'error', message: string) => void;
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in again to publish calendar updates.');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function statusLabel(status: CalendarPublicationStatus['status']) {
  if (status === 'cancellation_pending') return 'Cancellation pending';
  if (status === 'updates_pending') return 'Updates pending';
  return 'Up to date';
}

function statusColor(status: CalendarPublicationStatus['status']): 'success' | 'warning' | 'error' {
  if (status === 'cancellation_pending') return 'error';
  if (status === 'updates_pending') return 'warning';
  return 'success';
}

export function CalendarPublicationPanel({ kind, id, readOnly = false, onMessage }: CalendarPublicationPanelProps) {
  const [status, setStatus] = useState<CalendarPublicationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const loadStatus = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await fetch(`/api/player/calendar/publish-status?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`, {
        headers: await authHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to load calendar publish status.');
      setStatus({
        subscriberCount: Number(result.subscriberCount || 0),
        pendingCount: Number(result.pendingCount || 0),
        status: result.status || 'up_to_date',
        unreadyReason: result.unreadyReason || null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load calendar publish status.';
      setStatus(null);
      setLoadError(message);
      onMessageRef.current?.('error', message);
    } finally {
      setLoading(false);
    }
  }, [id, kind]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const publish = async () => {
    if (!status || status.pendingCount === 0) return;
    const action = status.status === 'cancellation_pending' ? 'send cancellation notices' : 'publish calendar updates';
    if (!window.confirm(`This will ${action} to ${status.pendingCount} subscribed player${status.pendingCount === 1 ? '' : 's'}. Continue?`)) return;
    setPublishing(true);
    try {
      const response = await fetch('/api/player/calendar/publish', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ kind, id }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to publish calendar updates.');
      const summary = result.summary || {};
      const sent = Number(summary.sent || 0);
      const failed = Number(summary.failed || 0);
      onMessageRef.current?.(failed > 0 ? 'warning' : 'success', failed > 0
        ? `Calendar publish completed with warnings. ${sent} sent, ${failed} failed.`
        : `Calendar updates published to ${sent} subscribed player${sent === 1 ? '' : 's'}.`);
      await loadStatus();
    } catch (error) {
      onMessageRef.current?.('error', error instanceof Error ? error.message : 'Unable to publish calendar updates.');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Checking player calendar subscribers…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={() => void loadStatus()}>Retry</Button>}
      >
        {loadError}
      </Alert>
    );
  }

  if (status?.unreadyReason) {
    return (
      <Alert severity="info" icon={<EventAvailableIcon />}>
        {status.unreadyReason}
        {status.subscriberCount > 0 ? ` ${status.subscriberCount} player${status.subscriberCount === 1 ? ' has' : 's have'} already subscribed.` : ''}
      </Alert>
    );
  }

  if (!status || status.subscriberCount === 0) {
    return (
      <Alert severity="info" icon={<EventAvailableIcon />}>
        No players have subscribed this item to their calendar yet.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Player Calendar Subscribers</Typography>
            <Chip size="small" label={`${status.subscriberCount} subscribed`} />
            <Chip size="small" color={statusColor(status.status)} label={statusLabel(status.status)} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Calendar emails are only sent when you deliberately publish updates or cancellations.
          </Typography>
        </Box>
        {!readOnly && status.pendingCount > 0 && (
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            disabled={publishing}
            onClick={() => void publish()}
          >
            {status.status === 'cancellation_pending' ? 'Publish Cancellation' : 'Publish Updates'}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
